import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Paper,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  ThumbUp as ThumbUpIcon,
  Share as ShareIcon,
  Visibility as ViewsIcon,
  Public as ReachIcon,
  Timeline as TimelineIcon,
  Download as DownloadIcon,
  DateRange as DateRangeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

// Import custom components
import { Card, MagicLoader } from '../../design-system';
import { Button } from '../../design-system/components/core';

// Import hooks and services
import useClientStore from '../../stores/clientStore';
import useNotificationStore from '../../stores/notificationStore';
import { getClientAnalytics, exportClientAnalyticsData, generateDemoClientAnalytics } from './ClientAnalyticsService';
import { formatNumber } from '../../utils/analyticsUtils';

// Helper component for metric cards
const MetricCard = ({ title, value, icon, change, isPercentage = false, color = 'primary' }) => {
  const theme = useTheme();
  const isPositive = change >= 0;
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {isPercentage ? `${value}%` : value.toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: alpha(theme.palette[color].main, 0.1),
              color: theme.palette[color].main,
              borderRadius: '50%',
              height: 48,
              width: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
        {change !== null && (
          <Box
            sx={{
              pt: 2,
              display: 'flex',
              alignItems: 'center',
              color: isPositive ? theme.palette.success.main : theme.palette.error.main,
            }}
          >
            {isPositive ? '↑' : '↓'} {Math.abs(change)}%
            <Typography
              color="textSecondary"
              variant="caption"
              sx={{ ml: 1 }}
            >
              from last period
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ClientAnalytics component
const ClientAnalytics = () => {
  const { id } = useParams();
  const theme = useTheme();
  
  // Store hooks
  const { 
    currentClient, 
    isLoading, 
    error, 
    fetchClient,
    fetchClientAnalytics 
  } = useClientStore();
  
  const { showError } = useNotificationStore();
  
  // Local state
  const [dateRange, setDateRange] = useState('last30days');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  
  // Fetch client details
  useEffect(() => {
    fetchClient(id).catch((error) => {
      showError('Failed to fetch client details: ' + error.message);
    });
  }, [id, fetchClient, showError]);
  
  // Fetch analytics data
  useEffect(() => {
    const loadAnalytics = async () => {
      if (!currentClient) return;
      
      setIsLoadingAnalytics(true);
      try {
        // Set date range parameters based on selection
        let params = {};
        const now = new Date();
        let startDate = new Date();
        
        switch (dateRange) {
          case 'last7days':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'last30days':
            startDate.setDate(now.getDate() - 30);
            break;
          case 'last90days':
            startDate.setDate(now.getDate() - 90);
            break;
          case 'last6months':
            startDate.setMonth(now.getMonth() - 6);
            break;
          case 'ytd':
            startDate = new Date(now.getFullYear(), 0, 1); // January 1st of current year
            break;
          default:
            startDate.setDate(now.getDate() - 30);
        }
        
        params.startDate = startDate.toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
        
        // Fetch analytics data from API or use demo data
        try {
          // First try to get real data
          const data = await getClientAnalytics(id, params);
          setAnalyticsData(data);
        } catch (apiError) {
          console.warn('Failed to fetch real data, using demo data', apiError);
          // Fall back to demo data if API fails
          const demoData = generateDemoClientAnalytics();
          setAnalyticsData(demoData);
        }
      } catch (error) {
        showError('Failed to load analytics: ' + error.message);
      } finally {
        setIsLoadingAnalytics(false);
      }
    };
    
    loadAnalytics();
  }, [id, dateRange, currentClient, showError]);
  
  // Handle date range change
  const handleDateRangeChange = (event) => {
    setDateRange(event.target.value);
  };
  
  // Handle export analytics
  const handleExportAnalytics = async () => {
    try {
      const now = new Date();
      let startDate = new Date();
      
      switch (dateRange) {
        case 'last7days':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'last30days':
          startDate.setDate(now.getDate() - 30);
          break;
        case 'last90days':
          startDate.setDate(now.getDate() - 90);
          break;
        case 'last6months':
          startDate.setMonth(now.getMonth() - 6);
          break;
        case 'ytd':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate.setDate(now.getDate() - 30);
      }
      
      const params = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: now.toISOString().split('T')[0],
        format: 'excel'
      };
      
      try {
        const data = await exportClientAnalyticsData(id, params);
        // Create a download link for the data
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${currentClient?.name || 'client'}-analytics-${now.toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (exportError) {
        console.error('Export error:', exportError);
        showError('Export functionality not available: ' + exportError.message);
      }
    } catch (error) {
      showError('Failed to export analytics: ' + error.message);
    }
  };
  
  // Loading states
  if (isLoading && !currentClient) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoader variant="mystical" size="large" message="Loading client information..." />
      </Box>
    );
  }
  
  // Get the data (real or fallback)
  const data = analyticsData || generateDemoClientAnalytics();
  
  // Define chart colors
  const CHART_COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link 
          underline="hover" 
          color="inherit" 
          component={RouterLink} 
          to="/clients"
        >
          Clients
        </Link>
        <Link 
          underline="hover" 
          color="inherit" 
          component={RouterLink} 
          to={`/clients/${id}`}
        >
          {currentClient?.name || 'Client'}
        </Link>
        <Typography color="text.primary">Analytics</Typography>
      </Breadcrumbs>
      
      {/* Page header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            {currentClient?.name || 'Client'} Analytics
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Performance metrics and audience insights
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="date-range-label">Date Range</InputLabel>
            <Select
              labelId="date-range-label"
              id="date-range-select"
              value={dateRange}
              onChange={handleDateRangeChange}
              label="Date Range"
              startAdornment={<DateRangeIcon sx={{ mr: 1 }} />}
            >
              <MenuItem value="last7days">Last 7 Days</MenuItem>
              <MenuItem value="last30days">Last 30 Days</MenuItem>
              <MenuItem value="last90days">Last 90 Days</MenuItem>
              <MenuItem value="last6months">Last 6 Months</MenuItem>
              <MenuItem value="ytd">Year to Date</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportAnalytics}
          >
            Export
          </Button>
        </Box>
      </Box>
      
      {/* Loading state */}
      {isLoadingAnalytics ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Overview metrics */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUpIcon sx={{ mr: 1 }} />
              Performance Overview
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Followers"
                  value={data.overview.followers.value}
                  icon={<GroupIcon />}
                  change={data.overview.followers.change}
                  
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Engagement Rate"
                  value={data.overview.engagement.value}
                  icon={<ThumbUpIcon />}
                  change={data.overview.engagement.change}
                  isPercentage={true}
                  color="secondary"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Reach"
                  value={data.overview.reach.value}
                  icon={<ReachIcon />}
                  change={data.overview.reach.change}
                  color="success"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Impressions"
                  value={data.overview.impressions.value}
                  icon={<ViewsIcon />}
                  change={data.overview.impressions.change}
                  color="info"
                />
              </Grid>
            </Grid>
          </Box>
          
          {/* Engagement trend chart */}
          <Box sx={{ mb: 4 }}>
            <Card title="Engagement Trend" magical={true} level={1}>
              <CardContent>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.engagementTrend}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => `${value}%`} />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="engagement" 
                        stroke={theme.palette.primary.main} 
                        fill={alpha(theme.palette.primary.main, 0.2)} 
                        name="Engagement Rate (%)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Box>
          
          {/* Content Performance and Platform Distribution */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={7}>
              <Card title="Content Performance" magical={true} level={1}>
                <CardContent>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.contentPerformance}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                        <XAxis dataKey="type" />
                        <YAxis yAxisId="left" orientation="left" stroke={theme.palette.primary.main} />
                        <YAxis yAxisId="right" orientation="right" stroke={theme.palette.secondary.main} />
                        <Tooltip />
                        <Legend />
                        <Bar 
                          yAxisId="left" 
                          dataKey="engagement" 
                          name="Avg. Engagement Rate (%)" 
                          fill={theme.palette.primary.main} 
                        />
                        <Bar 
                          yAxisId="right" 
                          dataKey="reach" 
                          name="Avg. Reach" 
                          fill={theme.palette.secondary.main} 
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Card title="Platform Distribution" magical={true} level={1}>
                <CardContent>
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.platformBreakdown}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {data.platformBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value}%`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Audience Demographics */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <GroupIcon sx={{ mr: 1 }} />
              Audience Demographics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card title="Age Distribution" magical={false}>
                  <CardContent>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={data.audienceDemographics.age}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                          <XAxis type="number" tickFormatter={(value) => `${value}%`} />
                          <YAxis dataKey="name" type="category" />
                          <Tooltip formatter={(value) => `${value}%`} />
                          <Bar dataKey="value" fill={theme.palette.primary.main} name="Percentage" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card title="Gender Distribution" magical={false}>
                  <CardContent>
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={data.audienceDemographics.gender}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {data.audienceDemographics.gender.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `${value}%`} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClientAnalytics;